/* eslint-disable import/no-unresolved */
import {BrowserAgent} from '@newrelic/browser-agent/loaders/browser-agent';
import {GenericEvents} from '@newrelic/browser-agent/features/generic_events';
import {PageViewEvent} from '@newrelic/browser-agent/features/page_view_event';
import {PageViewTiming} from '@newrelic/browser-agent/features/page_view_timing';
/* eslint-enable import/no-unresolved */

/**
 * Simple singleton to load the NR browser agent.
 *
 * @link https://docs.newrelic.com/docs/browser/browser-monitoring/installation/install-browser-monitoring-agent/
 */
class NewRelicBrowser {
  static instance = null;

  static load() {
    if (NewRelicBrowser.instance) {
      return NewRelicBrowser.instance;
    }

    if (!process.env.NEW_RELIC_LICENSE_KEY) {
      return null;
    }

    NewRelicBrowser.instance = new BrowserAgent({
      init: {
        distributed_tracing: {enabled: true},
        privacy: {cookies_enabled: true},
        /**
         * Prevents the agent from tracking its own beacon requests.
         */
        ajax: {deny_list: ['bam.nr-data.net']},
        session_replay: {enabled: false},
        logging: {enabled: false},
      },
      info: {
        beacon: 'bam.nr-data.net',
        errorBeacon: 'bam.nr-data.net',
        licenseKey: process.env.NEW_RELIC_LICENSE_KEY,
        applicationID: process.env.NEW_RELIC_APP_ID,
        sa: 1,
      },
      loader_config: {
        accountID: process.env.NEW_RELIC_ACCOUNT_ID,
        trustKey: process.env.NEW_RELIC_TRUST_KEY,
        agentID: process.env.NEW_RELIC_APP_ID,
        licenseKey: process.env.NEW_RELIC_LICENSE_KEY,
        applicationID: process.env.NEW_RELIC_APP_ID,
      },
      /**
       * PageViewEvent + PageViewTiming: Core Web Vitals (LCP, CLS, INP).
       * GenericEvents: custom PageActions (e.g. Gift Advisor perceived wait).
       */
      features: [PageViewEvent, PageViewTiming, GenericEvents],
    });

    return NewRelicBrowser.instance;
  }

  /**
   * Reports a browser PageAction when the agent is loaded.
   * No-ops when the license key is absent or the agent failed to init.
   *
   * @param {string} name
   * @param {Object} [attributes]
   */
  static addPageAction(name, attributes) {
    const agent = NewRelicBrowser.instance ?? NewRelicBrowser.load();

    if (!agent || typeof agent.addPageAction !== 'function') {
      return;
    }

    agent.addPageAction(name, attributes);
  }
}

NewRelicBrowser.load();

export default NewRelicBrowser;
